import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import Select from "react-select";
import "./StatsMeetings.css";
import "./StatsCharities.css";

const money = (cents) =>
    new Intl.NumberFormat("en-ZA", {
        style: "currency",
        currency: "ZAR",
        maximumFractionDigits: 0,
    }).format(Number(cents || 0) / 100);

const exportCsv = (filename, headers, rows) => {
    const escape = (value) => {
        const text = String(value ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toISOString().slice(0, 10);
};

function StatsCharities({ onClose }) {
    const [statsData, setStatsData] = useState({});
    const [filters, setFilters] = useState({
        from: "",
        to: "",
        category: "",
        lodge: "",
        status: "",
        source: "",
    });
    const [options, setOptions] = useState({ categories: [], lodges: [], statuses: [], sources: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const monthlyChartRef = useRef(null);
    const monthlyChartInstance = useRef(null);
    const categoryChartRef = useRef(null);
    const categoryChartInstance = useRef(null);
    const sourceChartRef = useRef(null);
    const sourceChartInstance = useRef(null);

    useEffect(() => {
        const loadOptions = async () => {
            const [categories, lodges, charities] = await Promise.all([
                axios.get("/api/resources/charity-categories"),
                axios.get("/api/resources/lodges"),
                axios.get("/api/resources/charities"),
            ]);

            const statusSet = new Set((charities.data || []).map((item) => item.status || "Active"));
            setOptions((prev) => ({
                ...prev,
                categories: (categories.data || []).map((item) => ({ value: item.id, label: item.name })),
                lodges: (lodges.data || []).map((item) => ({ value: item.id, label: item.name })),
                statuses: [...statusSet].sort().map((item) => ({ value: item, label: item })),
            }));
        };
        loadOptions().catch((err) => console.error("Failed to load charity stat filters:", err));
    }, []);

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.from, filters.to, filters.category, filters.lodge, filters.status, filters.source]);

    useEffect(() => {
        const sources = (statsData.bySource || []).map((row) => ({ value: row.label, label: row.label }));
        setOptions((prev) => ({ ...prev, sources }));
    }, [statsData.bySource]);

    const fetchStats = async () => {
        try {
            setIsLoading(true);
            setError("");
            const params = Object.fromEntries(
                Object.entries(filters).filter(([, value]) => String(value || "").trim())
            );
            const { data } = await axios.get("/api/stats/charities", { params });
            setStatsData(data || {});
        } catch (err) {
            console.error("Error fetching charity statistics:", err);
            setError("Failed to load charity statistics.");
        } finally {
            setIsLoading(false);
        }
    };

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value || "" }));
    const clearDateRange = () => setFilters((prev) => ({ ...prev, from: "", to: "" }));

    const summary = statsData.summary || {};
    const kpis = [
        ["Total Raised", money(summary.total_cents || 0)],
        ["Donations", summary.donation_count || 0],
        ["Funds", summary.total_funds || 0],
        ["Contributors", summary.contributing_lodges || 0],
        ["Need Attention", summary.needs_attention || 0],
    ];

    useEffect(() => {
        if (!monthlyChartRef.current || isLoading || error) return;
        if (monthlyChartInstance.current) monthlyChartInstance.current.destroy();
        const rows = statsData.monthly || [];

        monthlyChartInstance.current = new Chart(monthlyChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: rows.map((row) => row.month),
                datasets: [{
                    label: "Raised",
                    data: rows.map((row) => Number(row.total_cents || 0) / 100),
                    backgroundColor: "rgba(20, 184, 166, 0.70)",
                    borderColor: "rgba(15, 118, 110, 0.95)",
                    borderWidth: 1,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { ticks: { maxRotation: 55, minRotation: 35 } },
                    y: { beginAtZero: true, title: { display: true, text: "Rand" } },
                },
                plugins: { legend: { display: false }, title: { display: true, text: "Fundraising by Month" } },
            },
        });

        return () => monthlyChartInstance.current?.destroy();
    }, [error, isLoading, statsData.monthly]);

    useEffect(() => {
        if (!categoryChartRef.current || isLoading || error) return;
        if (categoryChartInstance.current) categoryChartInstance.current.destroy();
        const rows = statsData.byCategory || [];

        categoryChartInstance.current = new Chart(categoryChartRef.current.getContext("2d"), {
            type: "doughnut",
            data: {
                labels: rows.map((row) => row.label),
                datasets: [{
                    data: rows.map((row) => Number(row.total_cents || 0) / 100),
                    backgroundColor: ["#14b8a6", "#60a5fa", "#fb923c", "#a78bfa", "#f43f5e", "#22c55e", "#64748b"],
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "bottom" }, title: { display: true, text: "Giving by Fund Type" } },
            },
        });

        return () => categoryChartInstance.current?.destroy();
    }, [error, isLoading, statsData.byCategory]);

    useEffect(() => {
        if (!sourceChartRef.current || isLoading || error) return;
        if (sourceChartInstance.current) sourceChartInstance.current.destroy();
        const rows = statsData.bySource || [];

        sourceChartInstance.current = new Chart(sourceChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: rows.map((row) => row.label),
                datasets: [{
                    label: "Raised",
                    data: rows.map((row) => Number(row.total_cents || 0) / 100),
                    backgroundColor: "rgba(251, 146, 60, 0.72)",
                    borderColor: "rgba(194, 65, 12, 0.95)",
                    borderWidth: 1,
                }],
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                scales: { x: { beginAtZero: true } },
                plugins: { legend: { display: false }, title: { display: true, text: "Giving by Source" } },
            },
        });

        return () => sourceChartInstance.current?.destroy();
    }, [error, isLoading, statsData.bySource]);

    const exportSummary = () => {
        exportCsv("charity-statistics-summary.csv", ["Metric", "Value"], [
            ["Total Raised", money(summary.total_cents || 0)],
            ["Donations", summary.donation_count || 0],
            ["Funds", summary.total_funds || 0],
            ["Funds With Donations", summary.funds_with_donations || 0],
            ["Contributing Lodges", summary.contributing_lodges || 0],
            ["Average Donation", money(summary.average_donation_cents || 0)],
            ["Target Total", money(summary.total_target_cents || 0)],
            ["Target Progress", summary.target_progress_percent == null ? "" : `${summary.target_progress_percent}%`],
            ["Evidence Documents", summary.evidence_documents || 0],
            ["Needs Attention", summary.needs_attention || 0],
        ]);
    };

    const exportRankings = () => {
        exportCsv("charity-statistics-rankings.csv", ["Section", "Rank", "Label", "Donations", "Total"], [
            ...(statsData.byCharity || []).map((row, index) => ["Fund", index + 1, row.label, row.donation_count, money(row.total_cents)]),
            ...(statsData.byLodge || []).map((row, index) => ["Lodge / Entity", index + 1, row.label, row.donation_count, money(row.total_cents)]),
            ...(statsData.byCategory || []).map((row, index) => ["Category", index + 1, row.label, row.donation_count, money(row.total_cents)]),
            ...(statsData.bySource || []).map((row, index) => ["Source", index + 1, row.label, row.donation_count, money(row.total_cents)]),
        ]);
    };

    const exportDonations = () => {
        exportCsv("charity-statistics-donations.csv", ["Date", "Fund", "Category", "Lodge / Entity", "Amount", "Source", "Notes"], (
            statsData.donations || []
        ).map((row) => [
            formatDate(row.donated_on),
            row.charity_name,
            row.category_name,
            row.lodge_name || "Unspecified",
            money(row.amount_cents),
            row.source,
            row.notes || "",
        ]));
    };

    const exportAttention = () => {
        exportCsv("charity-statistics-needs-attention.csv", ["Fund", "Category", "Status", "Activity Date", "Total", "Reasons"], (
            statsData.needsAttention || []
        ).map((row) => [
            row.name,
            row.category_name,
            row.status,
            formatDate(row.activity_date),
            money(row.total_cents),
            (row.reasons || []).join("; "),
        ]));
    };

    const openCharitiesManager = () => {
        window.open("/resources", "_blank", "noopener,noreferrer");
    };

    return (
        <div className="st-modal-overlay">
            <div className="st-modal-content">
                <header className="st-modal-header">
                    <div>
                        <p className="st-eyebrow">Fundraising Intelligence</p>
                        <h2>Charity & Fundraising Statistics</h2>
                    </div>
                    <button className="st-modal-close-btn" onClick={onClose} aria-label="Close Charity Statistics">&times;</button>
                </header>

                <div className="st-modal-body st-dashboard">
                    <aside className="st-filter-panel">
                        <h3>Filters</h3>
                        <label>Start Date</label>
                        <input type="date" value={filters.from} onChange={(event) => setFilter("from", event.target.value)} />
                        <label>End Date</label>
                        <input type="date" value={filters.to} onChange={(event) => setFilter("to", event.target.value)} />
                        <button className="st-secondary-btn" type="button" onClick={clearDateRange}>All Dates</button>
                        <label>Category</label>
                        <Select
                            options={[{ value: "", label: "All Categories" }, ...options.categories]}
                            value={options.categories.find((item) => item.value === filters.category) || { value: "", label: "All Categories" }}
                            onChange={(option) => setFilter("category", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>Lodge / Entity</label>
                        <Select
                            options={[{ value: "", label: "All Lodges" }, ...options.lodges]}
                            value={options.lodges.find((item) => item.value === filters.lodge) || { value: "", label: "All Lodges" }}
                            onChange={(option) => setFilter("lodge", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>Status</label>
                        <Select
                            options={[{ value: "", label: "All Statuses" }, ...options.statuses]}
                            value={options.statuses.find((item) => item.value === filters.status) || { value: "", label: "All Statuses" }}
                            onChange={(option) => setFilter("status", option?.value)}
                            isClearable
                        />
                        <label>Source</label>
                        <Select
                            options={[{ value: "", label: "All Sources" }, ...options.sources]}
                            value={options.sources.find((item) => item.value === filters.source) || { value: "", label: "All Sources" }}
                            onChange={(option) => setFilter("source", option?.value)}
                            isClearable
                        />
                        <button className="st-primary-btn" onClick={fetchStats}>Refresh</button>
                        <button className="st-secondary-btn" onClick={exportSummary}>Export Summary CSV</button>
                        <button className="st-secondary-btn" onClick={exportRankings}>Export Rankings CSV</button>
                        <button className="st-secondary-btn" onClick={exportDonations}>Export Donations CSV</button>
                        <button className="st-secondary-btn" onClick={exportAttention}>Export Attention CSV</button>
                    </aside>

                    <main className="st-main-panel">
                        {isLoading && <div className="loading-spinner">Loading...</div>}
                        {error && <div className="error-message">{error}</div>}
                        {!isLoading && !error && (
                            <>
                                <section className="st-kpi-grid cs-kpi-grid">
                                    {kpis.map(([label, value]) => (
                                        <div key={label} className="st-kpi">
                                            <strong>{value}</strong>
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </section>

                                <section className="cs-health-strip">
                                    <div><strong>{summary.funds_with_donations || 0}/{summary.total_funds || 0}</strong><span>funds with donations</span></div>
                                    <div><strong>{money(summary.average_donation_cents || 0)}</strong><span>average donation</span></div>
                                    <div><strong>{money(summary.total_target_cents || 0)}</strong><span>target total</span></div>
                                    <div><strong>{summary.target_progress_percent == null ? "n/a" : `${summary.target_progress_percent}%`}</strong><span>target progress</span></div>
                                    <div><strong>{summary.evidence_documents || 0}</strong><span>linked evidence docs</span></div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Monthly Fundraising</h3>
                                            <span>{(statsData.monthly || []).length} months</span>
                                        </div>
                                        <div className="st-chart-container"><canvas ref={monthlyChartRef}></canvas></div>
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Giving by Fund Type</h3>
                                            <span>{(statsData.byCategory || []).length} categories</span>
                                        </div>
                                        <div className="st-chart-container"><canvas ref={categoryChartRef}></canvas></div>
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Giving by Source</h3>
                                            <span>fundraising source mix</span>
                                        </div>
                                        <div className="st-chart-container cs-source-chart"><canvas ref={sourceChartRef}></canvas></div>
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Fund Status</h3>
                                            <span>active and historical funds</span>
                                        </div>
                                        <StatsTable
                                            headers={["Status", "Funds", "Target"]}
                                            rows={(statsData.byStatus || []).map((row) => [row.label, row.fund_count, money(row.target_cents)])}
                                            empty="No status data."
                                        />
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Top Funds</h3>
                                            <span>by amount raised</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Fund", "Category", "Donations", "Total"]}
                                            rows={(statsData.byCharity || []).map((row, index) => [index + 1, row.label, row.category_name, row.donation_count, money(row.total_cents)])}
                                            empty="No fund donations recorded."
                                        />
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Top Lodges / Entities</h3>
                                            <span>contribution ranking</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Lodge / Entity", "Donations", "Total"]}
                                            rows={(statsData.byLodge || []).map((row, index) => [index + 1, row.label, row.donation_count, money(row.total_cents)])}
                                            empty="No lodge/entity contribution data."
                                        />
                                    </div>
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Fundraising Progress</h3>
                                        <span>funds with targets</span>
                                    </div>
                                    <StatsTable
                                        headers={["Fund", "Category", "Raised", "Target", "Progress", "Evidence"]}
                                        rows={(statsData.fundProgress || []).map((row) => [
                                            row.name,
                                            row.category_name,
                                            money(row.total_cents),
                                            money(row.target_amount_cents),
                                            `${row.progress_percent || 0}%`,
                                            row.evidence_count,
                                        ])}
                                        empty="No fund targets have been set yet."
                                    />
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Recent Donations</h3>
                                        <button className="st-mini-btn" onClick={exportDonations}>Export</button>
                                    </div>
                                    <StatsTable
                                        headers={["Date", "Fund", "Lodge / Entity", "Amount", "Source"]}
                                        rows={(statsData.donations || []).map((row) => [
                                            formatDate(row.donated_on),
                                            row.charity_name,
                                            row.lodge_name || "Unspecified",
                                            money(row.amount_cents),
                                            row.source,
                                        ])}
                                        empty="No donations in this range."
                                    />
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Needs Attention</h3>
                                        <div className="st-panel-actions">
                                            <span>{(statsData.needsAttention || []).length} funds</span>
                                            <button className="st-mini-btn" onClick={openCharitiesManager}>Open Manager</button>
                                        </div>
                                    </div>
                                    <StatsTable
                                        headers={["Fund", "Category", "Status", "Total", "Reasons"]}
                                        rows={(statsData.needsAttention || []).map((row) => [
                                            row.name,
                                            row.category_name,
                                            row.status,
                                            money(row.total_cents),
                                            (row.reasons || []).join(", "),
                                        ])}
                                        empty="No funds need attention."
                                    />
                                </section>
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

function StatsTable({ headers, rows, empty }) {
    return (
        <div className="st-table-wrap">
            <table className="st-stats-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr><td colSpan={headers.length}>{empty}</td></tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default StatsCharities;
